import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DiscussionSection from '../components/grammar-practice/DiscussionSection';
import { Exercise } from '../types/grammarPractice';
import { FaArrowLeft } from 'react-icons/fa';

/**
 * Standalone page for exercise discussions
 * Shows the discussion section for a specific exercise
 */
const ExerciseDiscussionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { exerciseId } = useParams<{ exerciseId: string }>();
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadExercise = () => {
      if (!exerciseId) {
        setError(t('errors.noExerciseId'));
        setLoading(false);
        return;
      }

      // Get exercise from navigation state
      const stateExercise = location.state?.exercise as Exercise | undefined;

      if (stateExercise) {
        setExercise(stateExercise);
        setLoading(false);
      } else {
        setError(t('errors.exerciseNotFound'));
        setLoading(false);
      }
    };

    loadExercise();
  }, [exerciseId, location.state, t]);

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with back button */}
      <div className="fixed top-16 left-0 right-0 bg-base-200 border-b border-base-300 z-30">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={handleBack} className="btn btn-ghost btn-sm gap-2">
            <FaArrowLeft />
            <span className="hidden sm:inline">{t('common.backToExercise')}</span>
          </button>
          {exercise && (
            <span className="text-sm text-base-content/70 truncate max-w-xs">
              {exercise.grammar_topic_name}
            </span>
          )}
        </div>
      </div>

      {/* Content with padding for header */}
      <div className="flex-1 overflow-y-auto pt-28 pb-20">
        <div className="container mx-auto p-4 max-w-4xl">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-error">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-current shrink-0 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Exercise Info Card */}
          {!loading && !error && exercise && (
            <>
              <div className="card bg-base-200 shadow-xl mb-6">
                <div className="card-body">
                  <p className="text-sm text-base-content/60 mb-2">
                    {exercise.grammar_topic_name}
                  </p>
                  <p className="text-xl font-semibold">{exercise.question}</p>
                </div>
              </div>

              {/* Discussion Section */}
              <DiscussionSection exerciseId={parseInt(exerciseId!, 10)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseDiscussionPage;
